import { clsx } from 'clsx';
import type { ReactNode } from 'react';

/* -------------------------------------------------------------------------
   ResourceTable

   Dense but legible (§7). The choices that matter:

     - 32px rows and 12px text: a licensing operator scans hundreds of rows, and
       comfortable spacing costs them a screenful of context per scroll.
     - `align: 'right'` for numerics, paired with the tabular figures set on
       body, so columns of numbers line up on the decimal.
     - the header is sticky and the wrapper scrolls horizontally, so a wide table
       never forces the page itself to scroll sideways.
     - a column may `pin: 'right'`. Row actions are the one thing an operator
       must always be able to reach, and they are conventionally last — which at
       a narrow width puts them furthest into the part that has scrolled away.
       Pinning keeps them against the edge while the middle of the table moves
       underneath.
     - `caption` is visually hidden but present, because a screen-reader user
       arriving at a bare grid of ids has no idea what they are reading.
   ------------------------------------------------------------------------- */

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  align?: 'left' | 'right';
  /** Tailwind width utility, e.g. 'w-40'. */
  width?: string;
  /**
   * Holds this column against the right edge while the rest scrolls under it.
   *
   * For row actions. A table wide enough to scroll puts its last column off
   * screen, and if that column is the only way to edit or archive the row, the
   * operator sees no actions at all until they discover the scrollbar. Found at
   * 1024, where the environments table is 899px inside a 730px container and the
   * action buttons sat 128px past the visible edge.
   */
  pin?: 'right';
}

export interface ResourceTableProps<T> {
  caption: string;
  columns: ReadonlyArray<Column<T>>;
  rows: readonly T[];
  rowKey: (row: T) => string;
  /** Rendered in place of the table body when `rows` is empty. */
  empty?: ReactNode;
  className?: string;
}

export function ResourceTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  empty,
  className,
}: ResourceTableProps<T>) {
  if (rows.length === 0 && empty) {
    return <>{empty}</>;
  }

  return (
    <div className={clsx('w-full overflow-x-auto', className)}>
      <table className="w-full border-collapse text-left">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b border-[var(--vc-border)]">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={clsx(
                  'bg-[var(--vc-surface)] px-4 py-2 text-[11px] font-medium',
                  'tracking-[0.03em] whitespace-nowrap text-[var(--vc-text-subtle)] uppercase',
                  column.align === 'right' && 'text-right',
                  // Opaque, or the scrolling content shows through it.
                  column.pin === 'right' &&
                    'sticky right-0 z-10 bg-[var(--vc-surface)] shadow-[-8px_0_8px_-8px_var(--vc-border)]',
                  column.width,
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={clsx(
                'border-b border-[var(--vc-border-subtle)] last:border-b-0',
                'transition-colors duration-[var(--vc-duration-fast)] hover:bg-[var(--vc-surface-hover)]',
              )}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={clsx(
                    'px-4 py-2 text-[12px] text-[var(--vc-text)]',
                    column.align === 'right' && 'text-right',
                    column.pin === 'right' &&
                      'sticky right-0 z-10 bg-[var(--vc-surface)] shadow-[-8px_0_8px_-8px_var(--vc-border)]',
                  )}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
